import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchOrders } from '../apiEndpoints';
import ConcertTicket from './ConcertTicket';
import { currencySymbol } from '@/utils/currency';
import { IconChevronLeft as ChevronLeft, IconChevronRight as ChevronRight } from '@tabler/icons-react';
import { eyebrow, sectionTitle } from '@/components/ui/design';
import { Select, SelectItem } from '@/components/ui/AppSelect';

export default function MyTicketsTab() {
    const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
    const [page, setPage] = useState(1); // Start from page 1
    const [rowsPerPage, setRowsPerPage] = useState(10);
    // Helper function to safely format date
    const formatDate = (dateString: string | undefined | null): string => {
        if (!dateString) return 'TBD';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'TBD';
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'TBD';
        }
    };

    // Helper function to safely format time
    const formatTime = (startDate: string | undefined | null, endDate: string | undefined | null): string => {
        if (!startDate || !endDate) return 'TBD';
        try {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'TBD';

            const startTime = start.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            const endTime = end.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            return `${startTime} to ${endTime}`;
        } catch (error) {
            console.error('Error formatting time:', error);
            return 'TBD';
        }
    };

    // Helper function to safely format address - show only fullAddress
    const formatAddress = (address: any): string => {

        if (!address) return '';

        // Handle JSON string format from API
        if (typeof address === 'string') {
            let cleanedAddress = address
                .trim()                                    // Remove leading/trailing whitespace
                .replace(/[\r\n\t]/g, '')                  // Remove newlines, carriage returns, tabs
                .replace(/[\u200B-\u200D\uFEFF]/g, '')     // Remove zero-width characters
                .replace(/^[^{]*/, '')                     // Remove everything before first {
                .replace(/[^}]*$/, '')                     // Remove everything after last }
                .trim();

            // Fix common JSON formatting issues
            cleanedAddress = cleanedAddress
                .replace(/\s*{\s*/g, '{')           // Remove spaces around opening brace
                .replace(/\s*}\s*/g, '}')           // Remove spaces around closing brace
                .replace(/\s*:\s*/g, ':')           // Remove spaces around colons
                .replace(/\s*,\s*/g, ',')           // Remove spaces around commas
                .replace(/\s*'\s*/g, "'")           // Remove spaces around single quotes
                .replace(/\s*"\s*/g, '"');          // Remove spaces around double quotes

            // Convert single quotes to double quotes for valid JSON
            cleanedAddress = cleanedAddress
                .replace(/'/g, '"');                // Replace all single quotes with double quotes

            // Ensure property keys are surrounded by double quotes
            cleanedAddress = cleanedAddress
                .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')  // Add quotes around property names
                .replace(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*:/, '"$1":');             // Handle first property

            try {
                // Clean the address string more thoroughly
                console.log('Cleaned address:', cleanedAddress);
                const parsedAddress = JSON.parse(cleanedAddress);

                return parsedAddress.fullAddress;
            } catch (error) {
                console.error('Error parsing address JSON:', error);
                console.error('Problematic string:', JSON.stringify(cleanedAddress));
                return ''; // Return empty string if parsing fails
            }
        }

        // Handle object format
        return address.fullAddress;
    };

    // Fetch user orders (tickets)
    const { data, isLoading, isError } = useQuery({
        queryKey: ['userOrders', page, rowsPerPage, tab],
        queryFn: () => fetchOrders({ page, limit: rowsPerPage, type: tab }),
    });

    const upcomingCount = useQuery({
        queryKey: ['userOrdersCount', 'upcoming'],
        queryFn: () => fetchOrders({ page: 1, limit: 1, type: 'upcoming' }),
    });
    const pastCount = useQuery({
        queryKey: ['userOrdersCount', 'past'],
        queryFn: () => fetchOrders({ page: 1, limit: 1, type: 'past' }),
    });

    // Derived directly from the current tab's query result (not copied into local
    // state) so switching tabs can never leave a previous tab's data on screen.
    const orders = data?.data?.data || [];
    const totalRows = data?.data?.total || 0;

    // Map orders to ticket UI format with proper data structure
    const tickets = orders.flatMap((order: any) => {
        // Each order may have multiple tickets
        return (order.tickets || []).map((ticket: any, ticketIdx: number) => {
            // Since event data is null, use ticket data directly
            const ticketId = ticket.ticketId || {};
            const eventTitle = ticket.eventTitle || ticket.event?.title || 'Event';
            const eventAddress = ticket.eventAddress || '';
            const selectedDate = ticket.selectedDate || '';

            // Parse event address from JSON string
            const addressText = formatAddress(eventAddress);
            console.log('wwww6', addressText, eventAddress);
            // Format date from selectedDate
            const eventDate = selectedDate;

            // For time, we'll use a default since we don't have event times
            const eventTime = 'TBD';

            // Check if event is past based on selectedDate
            const isPast = selectedDate ? new Date(selectedDate) < new Date() : false;

            return {
                _id: `${order?._id || order?.orderId}-${ticket._id || ticketIdx}`,
                image: ticket.event?.banner?.link || '/event1.png', // Default image since we don't have event banner
                title: eventTitle,
                date: eventDate,
                time: eventTime,
                location: addressText,
                type: ticketId?.name || ticket?.type || 'General Admission',
                price: ticket?.price ? `${currencySymbol(order?.currency)}${ticket.price}` : '-',
                order: order?.orderId || order?._id || '',
                orderHash: order?.hashedUniqueId || '',
                name: order?.customerName || '',
                eventPage: `/event/${ticket.eventId || ''}`,
                isPast: isPast,
                qrCode: ticket?.qrCode || '',
                eventId: ticket.eventId || '',
                ticketData: ticket,
                orderData: order,
                eventData: null // Event data is not available in current API response
            };
        }).filter((ticket: any) => ticket !== null); // Remove null tickets
    });

    const filteredTickets = tickets.filter((ticket: any) => {
        return true;
    });

    const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

    // Handle page changes
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            scrollTo(0, 0);
        }
    };

    // Handle rows per page changes
    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(1); // Reset to first page when changing rows per page
    };

    const tabItems: { key: 'upcoming' | 'past'; label: string; count: number | undefined }[] = [
        { key: 'upcoming', label: 'Future', count: upcomingCount.data?.data?.total },
        { key: 'past', label: 'Past', count: pastCount.data?.data?.total },
    ];

    return (
        <div className="w-full">
            <p className={eyebrow}>Your passes</p>
            <h1 className={`${sectionTitle} mt-3 mb-6 md:mb-8`}>My Tickets</h1>

            <div className="mb-8 inline-flex rounded-full bg-grey99 p-1 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06] md:mb-10">
                {tabItems.map(item => {
                    const active = tab === item.key;
                    return (
                        <button
                            key={item.key}
                            onClick={() => {
                                setPage(1);
                                setTab(item.key);
                            }}
                            className={`flex h-9 items-center gap-2 rounded-full px-4 text-sm font-semibold whitespace-nowrap transition-all ${active ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg' : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'}`}
                        >
                            {item.label}
                            {typeof item.count === 'number' && (
                                <span className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-bold ${active ? 'bg-white/20 text-white dark:bg-nightBg/10 dark:text-nightBg' : 'bg-grey95 text-grey20 dark:bg-white/10 dark:text-nightTextSecondary'}`}>
                                    {item.count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {isLoading ? (
                <div className="flex w-full items-center justify-center rounded-[28px] bg-grey99 py-16 text-base text-grey40 dark:bg-white/[0.04] dark:text-nightMuted">Loading tickets...</div>
            ) : isError ? (
                <div className="flex w-full items-center justify-center rounded-[28px] bg-red-50 px-6 py-16 text-center text-base font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">Failed to load tickets. Please try again.</div>
            ) : !data ? null : filteredTickets.length === 0 ? (
                <div className="flex w-full flex-col items-center justify-center rounded-[28px] bg-grey99 px-6 py-20 text-center dark:bg-white/[0.04]">
                    <div className="text-[clamp(24px,3.5vw,36px)] font-bold tracking-[-0.04em] text-grey10 dark:text-white">No {tab === 'upcoming' ? 'upcoming' : 'past'} tickets</div>
                    <div className="mt-2 text-base text-grey40 dark:text-nightMuted">Tickets you buy will show up here.</div>
                </div>
            ) : (
                <div key={tab} className="flex flex-col gap-8 md:gap-12">
                    {filteredTickets.map((ticket: any, idx: number) => {
                        const ticketData = ticket?.ticketData || {};
                        const ticketId = ticketData?.ticketId || {};
                        const event = ticketData?.event || {};
                        return (
                            <ConcertTicket
                                key={ticket._id || `ticket-${idx}`}
                                event={event}
                                seatData={ticketData?.seatData || null}
                                id={ticket.orderHash || ticket.order}
                                eventTitle={ticket?.title || 'Event Title Not Available'}
                                selectedDate={ticketData?.selectedDate || 'TBD'}
                                venue={event?.venue || ''}
                                address={ticket?.location || ''}
                                ticketType={ticketId?.name || ticketData?.type || 'General Admission'}
                                price={`${ticket?.price?.replace('$', '') || 0}`}
                                orderNumber={ticket?.order || 'N/A'}
                                orderHashedId={ticket?.orderHash || 'N/A'}
                                ticketQr={ticket?.qrCode || ''}
                                ticketNumber={ticket?.qrCode || ''}
                                attendeeName={ticket?.name || 'Guest'}
                                eventImage={ticket?.image || '/event1.png'}
                                eventId={ticket?.eventId || ''}
                            />
                        );
                    })}
                </div>
            )}

            {totalRows > rowsPerPage && (
                <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-[28px] bg-grey99 p-3 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                    <div className="flex items-center gap-3 pl-1 text-sm text-grey40 dark:text-nightMuted">
                        <Select classNames={{ trigger: 'h-10 px-3 text-sm' }} aria-label="Select" selectedKeys={[String(rowsPerPage ?? '')]} onChange={e => handleRowsPerPageChange(Number(e.target.value))}>
                            <SelectItem key={String(10)}>10 per page</SelectItem>
                            <SelectItem key={String(25)}>25 per page</SelectItem>
                            <SelectItem key={String(50)}>50 per page</SelectItem>
                        </Select>
                        <span>
                            {(page - 1) * rowsPerPage + 1} - {Math.min(page * rowsPerPage, totalRows)} of {totalRows}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-grey99 text-grey10 ring-1 ring-black/[0.04] transition-colors hover:bg-grey95 disabled:opacity-40 dark:bg-white/[0.06] dark:text-white dark:ring-white/[0.06] dark:hover:bg-white/10"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            aria-label="Previous page"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        {Array.from({ length: Math.min(4, totalPages) }, (_, i) => {
                            const pageNum = i + Math.max(1, page - 1);
                            return pageNum <= totalPages ? (
                                <button
                                    key={pageNum}
                                    className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition-colors ${page === pageNum ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg' : 'text-grey20 hover:bg-white dark:text-nightTextSecondary dark:hover:bg-white/10'}`}
                                    onClick={() => handlePageChange(pageNum)}
                                >
                                    {pageNum}
                                </button>
                            ) : null;
                        })}
                        <button
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-grey99 text-grey10 ring-1 ring-black/[0.04] transition-colors hover:bg-grey95 disabled:opacity-40 dark:bg-white/[0.06] dark:text-white dark:ring-white/[0.06] dark:hover:bg-white/10"
                            disabled={page === totalPages}
                            onClick={() => handlePageChange(page + 1)}
                            aria-label="Next page"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
